// src/components/ChatAssistant.tsx
import { useState, useRef, useEffect } from "react";
import {
  Stack,
  TextInput,
  Button,
  Paper,
  Text,
  ScrollArea,
  Box,
  ActionIcon,
  Loader,
  Avatar,
  Group,
  Alert,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import {
  IconRobot,
  IconSend,
  IconX,
  IconMessageCircle,
  IconWifiOff,
  IconTrash,
  IconChevronUp,
  IconMinimize,
} from "@tabler/icons-react";
import { useAIChat } from "../hooks/useAIChat";

interface ChatAssistantProps {
  restaurantId: string;
}

const suggestedQuestions = [
  { emoji: "🔥", text: "Popular items" },
  { emoji: "🌱", text: "Vegetarian" },
  { emoji: "⭐", text: "Best sellers" },
  { emoji: "🌶️", text: "Spicy food" },
  { emoji: "🍽️", text: "Combos" },
  { emoji: "💰", text: "Combo for 10" },
  { emoji: "👥", text: "Meal for 6" },
];

export function ChatAssistant({ restaurantId }: ChatAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const scrollViewport = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useMantineTheme();

  const {
    messages,
    isLoading,
    isTyping,
    sendMessage,
    clearHistory,
    messagesEndRef,
  } = useAIChat(restaurantId);

  // Check if mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    const handleResize = () => {
      // Re-trigger scroll on resize
      if (scrollViewport.current && isOpen && !isMinimized) {
        setTimeout(() => {
          scrollViewport.current?.scrollTo({
            top: scrollViewport.current.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, isMinimized]);

  useEffect(() => {
    if (scrollViewport.current && isOpen && !isMinimized) {
      setTimeout(() => {
        scrollViewport.current?.scrollTo({
          top: scrollViewport.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [messages, isOpen, isMinimized]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [isOpen, isMinimized]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || isOffline) return;
    const message = inputValue;
    setInputValue("");
    await sendMessage(message);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearHistory = () => {
    if (confirm("Clear chat history?")) {
      clearHistory();
    }
  };

  // Mobile: Full screen, Desktop: Floating window
  const getChatStyles = () => {
    if (isMobile) {
      return {
        position: "fixed" as const,
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        borderRadius: 0,
        zIndex: 1001,
      };
    }

    return {
      position: "fixed" as const,
      bottom: 20,
      right: 20,
      width: isMinimized ? 320 : 420,
      height: isMinimized ? 60 : 600,
      maxHeight: "calc(100vh - 40px)",
      borderRadius: theme.radius.lg,
      zIndex: 1001,
    };
  };

  return (
    <>
      {/* Chat Button - Floating bubble */}
      {!isOpen && (
        <Tooltip label="Ask AI Assistant" position="left">
          <ActionIcon
            variant="filled"
            color="orange"
            onClick={() => setIsOpen(true)}
            size={isMobile ? 50 : 55}
            radius="xl"
            style={{
              position: "fixed",
              bottom: isMobile ? 70 : 80,
              right: isMobile ? 16 : 20,
              zIndex: 1000,
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              transition: "transform 0.2s, box-shadow 0.2s",
              width: isMobile ? 50 : 55,
              height: isMobile ? 50 : 55,
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.2)";
              }
            }}
          >
            <IconMessageCircle size={isMobile ? 24 : 28} />
          </ActionIcon>
        </Tooltip>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Paper
          shadow={isMobile ? "none" : "xl"}
          radius={isMobile ? 0 : "lg"}
          style={{
            ...getChatStyles(),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.3s ease",
            backgroundColor: "var(--mantine-color-body)",
            border: isMobile ? "none" : "1px solid var(--mantine-color-gray-3)",
          }}
        >
          {/* Header */}
          <Box
            style={{
              padding: isMobile ? "14px" : "12px 16px",
              backgroundColor: "var(--mantine-color-orange-6)",
              color: "white",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
            onClick={() => !isMobile && setIsMinimized(!isMinimized)}
          >
            {/* Left Section */}
            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <Avatar
                color="white"
                size={isMobile ? "md" : "sm"}
                radius="xl"
                style={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  flexShrink: 0,
                }}
              >
                <IconRobot size={isMobile ? 18 : 16} />
              </Avatar>

              <Box style={{ minWidth: 0 }}>
                <Group gap={6} align="center" wrap="nowrap">
                  <Text
                    fw={600}
                    size={isMobile ? "md" : "sm"}
                    c="white"
                    truncate
                  >
                    AI Assistant
                  </Text>

                  {isTyping && !isMinimized && (
                    <Loader size="xs" variant="dots" color="white" />
                  )}
                </Group>

                <Text
                  size="xs"
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  Powered by Cloudflare Workers AI • Developed by xpoundAI
                </Text>
              </Box>
            </Group>

            {/* Right Section */}
            <Group gap={4} wrap="nowrap">
              {!isMobile && (
                <Tooltip label={isMinimized ? "Expand" : "Minimize"}>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    style={{ color: "white" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMinimized(!isMinimized);
                    }}
                  >
                    {isMinimized ? (
                      <IconChevronUp size={16} />
                    ) : (
                      <IconMinimize size={16} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}

              <Tooltip label="Clear History">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  style={{ color: "white" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearHistory();
                  }}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label="Close">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  style={{ color: "white" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>

          {/* Chat Content - Only show when not minimized */}
          {!isMinimized && (
            <>
              {/* Offline Warning */}
              {isOffline && (
                <Alert
                  icon={<IconWifiOff size={14} />}
                  title="Offline"
                  color="yellow"
                  variant="light"
                  withCloseButton
                  onClose={() => setIsOffline(false)}
                  style={{ borderRadius: 0 }}
                >
                  <Text size="xs">AI unavailable offline</Text>
                </Alert>
              )}

              {/* Messages Area */}
              <ScrollArea
                viewportRef={scrollViewport}
                style={{ flex: 1 }}
                type="auto"
                p={isMobile ? "md" : "md"}
              >
                <Stack gap="md">
                  {messages.map((message) => (
                    <Group
                      key={message.id}
                      justify={message.isUser ? "flex-end" : "flex-start"}
                      align="flex-start"
                      wrap="nowrap"
                      style={{ animation: "fadeIn 0.3s ease-out" }}
                    >
                      {!message.isUser && (
                        <Avatar
                          color="orange"
                          size={isMobile ? "sm" : "sm"}
                          radius="xl"
                        >
                          <IconRobot size={12} />
                        </Avatar>
                      )}
                      <Paper
                        p={isMobile ? "sm" : "sm"}
                        radius="md"
                        style={{
                          maxWidth: isMobile ? "80%" : "75%",
                          backgroundColor: message.isUser
                            ? "var(--mantine-color-orange-6)"
                            : "var(--mantine-color-gray-1)",
                          color: message.isUser
                            ? "white"
                            : "var(--mantine-color-dark-8)",
                        }}
                      >
                        <Text
                          size={isMobile ? "sm" : "sm"}
                          style={{ whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                        >
                          {message.text}
                        </Text>
                        <Text size="xs" opacity={0.6} mt={4}>
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Text>
                      </Paper>
                      {message.isUser && (
                        <Avatar
                          color="blue"
                          size={isMobile ? "sm" : "sm"}
                          radius="xl"
                        >
                          You
                        </Avatar>
                      )}
                    </Group>
                  ))}

                  {/* Typing indicator */}
                  {isLoading && (
                    <Group justify="flex-start" align="flex-start">
                      <Avatar
                        color="orange"
                        size={isMobile ? "sm" : "sm"}
                        radius="xl"
                      >
                        <IconRobot size={12} />
                      </Avatar>
                      <Paper p="sm" radius="md" bg="gray.1">
                        <Group gap={6}>
                          <div
                            className="typing-dot"
                            style={{ animationDelay: "0s" }}
                          />
                          <div
                            className="typing-dot"
                            style={{ animationDelay: "0.2s" }}
                          />
                          <div
                            className="typing-dot"
                            style={{ animationDelay: "0.4s" }}
                          />
                          <Text size="xs" c="dimmed" ml={4}>
                            AI is thinking...
                          </Text>
                        </Group>
                      </Paper>
                    </Group>
                  )}

                  <div ref={messagesEndRef} />
                </Stack>
              </ScrollArea>

              {/* Suggested Questions */}
              {messages.length <= 2 && !isOffline && !isLoading && (
                <Box
                  p="md"
                  style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
                >
                  <Text size="xs" c="dimmed" mb="xs">
                    Quick questions:
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    {suggestedQuestions.map((question) => (
                      <Button
                        key={question.text}
                        size="compact-xs"
                        variant="light"
                        onClick={() => {
                          setInputValue(question.text);
                          setTimeout(() => handleSend(), 100);
                        }}
                        leftSection={<span>{question.emoji}</span>}
                      >
                        {question.text}
                      </Button>
                    ))}
                  </Group>
                </Box>
              )}

              {/* Input Area */}
              <Box
                p="md"
                style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
              >
                <Group align="flex-end" gap="xs">
                  <TextInput
                    ref={inputRef}
                    placeholder={
                      isOffline
                        ? "Offline"
                        : isLoading
                          ? "AI is thinking..."
                          : "Ask about menu, combos..."
                    }
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    style={{ flex: 1 }}
                    size={isMobile ? "md" : "sm"}
                    disabled={isOffline}
                    radius="xl"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isOffline}
                    loading={isLoading}
                    variant="filled"
                    color="orange"
                    size={isMobile ? "md" : "sm"}
                    radius="xl"
                  >
                    <IconSend size={isMobile ? 18 : 16} />
                  </Button>
                </Group>
                {!isMobile && (
                  <Text size="xs" c="dimmed" mt="xs" ta="center">
                    {isLoading
                      ? "🤖 AI is generating response..."
                      : "💡 Ask about menu, combos, or get recommendations"}
                  </Text>
                )}
              </Box>
            </>
          )}
        </Paper>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .typing-dot {
          width: 8px;
          height: 8px;
          background-color: #999;
          border-radius: 50%;
          display: inline-block;
          animation: typingBounce 1.4s infinite ease-in-out;
        }
        
        @keyframes typingBounce {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-8px);
          }
        }
        
        /* Mobile adjustments */
        @media (max-width: 768px) {
          .typing-dot {
            width: 6px;
            height: 6px;
          }
        }
      `}</style>
    </>
  );
}
